import { getFunctions, httpsCallable } from 'firebase/functions';
import {
  doc,
  getFirestore,
  onSnapshot,
  serverTimestamp,
  setDoc,
  arrayUnion,
} from 'firebase/firestore';
import { functions as configuredFunctions } from '../config/firebase';
import { sanitizeRiderHtmlForExport } from './writerFormat';

function normalizeEmail(email) {
  return String(email || '').trim().toLowerCase();
}

export function buildRiderDayShareLink({ baseUrl, ownerId, tourId, dateKey }) {
  const origin = String(baseUrl || 'https://venuereportcard.com').replace(/\/$/, '');
  return `${origin}/calendar/day/${encodeURIComponent(dateKey)}?uid=${encodeURIComponent(
    ownerId
  )}&tour=${encodeURIComponent(tourId)}&modules=RIDER`;
}

export function riderShareDocId(tourId, dateKey, tabKey = 'RIDER_DAILY') {
  return `${tourId}__${dateKey}__${tabKey}`;
}

export function subscribeRiderShares(db, ownerId, tourId, dateKey, tabKey, callback) {
  if (!ownerId || !tourId || !dateKey) {
    callback([]);
    return () => {};
  }
  const ref = doc(db, 'users', ownerId, 'calendarRiderShares', riderShareDocId(tourId, dateKey, tabKey));
  return onSnapshot(
    ref,
    (snap) => {
      const shares = Array.isArray(snap.data()?.shares) ? snap.data().shares : [];
      callback(shares);
    },
    () => callback([])
  );
}

export async function recordRiderShares(db, ownerId, { tourId, dateKey, tabKey, emails, sentBy, shareLink }) {
  const normalized = [...new Set((emails || []).map(normalizeEmail).filter(Boolean))];
  if (!ownerId || !tourId || !dateKey || normalized.length === 0) return;

  const ref = doc(db, 'users', ownerId, 'calendarRiderShares', riderShareDocId(tourId, dateKey, tabKey));
  const now = new Date().toISOString();
  const entries = normalized.map((email) => ({
    email,
    sentAt: now,
    sentBy: sentBy || ownerId,
    shareLink: shareLink || '',
  }));

  await setDoc(
    ref,
    {
      tourId,
      dateKey,
      tabKey,
      shares: arrayUnion(...entries),
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );
}

export async function sendRiderShareEmail(recipient, emailData) {
  const handleEmail = httpsCallable(configuredFunctions || getFunctions(), 'handleEmail');
  await handleEmail({
    template: 'rider_share',
    recipient: normalizeEmail(recipient),
    emailData,
  });
}

function normalizeRiderRecipients(recipients) {
  const rows = [];
  (recipients || []).forEach((entry) => {
    if (typeof entry === 'string') {
      const email = normalizeEmail(entry);
      if (email) rows.push({ email, name: '' });
      return;
    }
    const email = normalizeEmail(entry?.email);
    if (!email) return;
    rows.push({
      email,
      name: String(entry?.name || '').trim(),
      userId: String(entry?.userId || '').trim(),
    });
  });
  const byEmail = new Map();
  rows.forEach((row) => {
    if (!byEmail.has(row.email)) byEmail.set(row.email, row);
    else if (!byEmail.get(row.email).name && row.name) byEmail.get(row.email).name = row.name;
  });
  return [...byEmail.values()];
}

export async function sendRiderToEmails({
  db,
  ownerId,
  ownerName,
  recipients,
  tourId,
  tourName,
  dateKey,
  dateLabel,
  tabKey,
  riderHtml,
  shareLink,
  message = '',
}) {
  const list = normalizeRiderRecipients(recipients);
  if (!list.length) throw new Error('Add at least one recipient.');

  const exportHtml = sanitizeRiderHtmlForExport(riderHtml);
  const failures = [];
  const successes = [];

  for (const person of list) {
    const { email, name } = person;
    try {
      await sendRiderShareEmail(email, {
        recipientName: name || email.split('@')[0],
        senderName: ownerName || 'Tour team',
        tourName: tourName || 'Tour',
        dateLabel: dateLabel || dateKey,
        shareLink,
        message,
        riderHtml: exportHtml,
      });
      successes.push(email);
    } catch (err) {
      console.warn('rider share email failed', email, err);
      failures.push(email);
    }
  }

  if (failures.length === list.length) {
    throw new Error('Could not send rider emails. Check your connection and try again.');
  }

  await recordRiderShares(db, ownerId, {
    tourId,
    dateKey,
    tabKey,
    emails: successes,
    sentBy: ownerId,
    shareLink,
  });

  if (failures.length) {
    throw new Error(
      `Sent to ${successes.length} of ${list.length} recipients. Failed: ${failures.join(', ')}`
    );
  }
}

export function parseEmailList(raw) {
  return [...new Set(String(raw || '').split(/[,;\s]+/).map(normalizeEmail).filter(Boolean))];
}

export function getFirestoreDb() {
  return getFirestore();
}
